import { useEffect, useState, useCallback } from 'react'
import { Spinner } from '@contentful/f36-spinner'
import { client } from '../src/App'

const SPACE_ID = process.env.CONTENTFUL_SPACE_ID

// Strip leading "Test " prefix from content type names
const formatContentTypeName = (name) => name.replace(/^Test\s+/, '')

const formatRelativeTime = (timestamp) => {
  const now = new Date()
  const updatedDate = new Date(timestamp)
  const diffMs = now - updatedDate
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return updatedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: now.getFullYear() !== updatedDate.getFullYear() ? 'numeric' : undefined,
  })
}

// Try a range of common name fields; return trimmed string or null
const getEntryName = (fields) => {
  if (!fields) return null
  const raw =
    fields.name ||
    fields.internalName ||
    fields.title ||
    fields.headline ||
    fields.label ||
    null
  if (!raw) return null
  const str = typeof raw === 'object' ? Object.values(raw)[0] : String(raw)
  return str.trim() || null
}

const RecentActivityPage = () => {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Content type filter
  const [contentTypes, setContentTypes] = useState([])
  const [ctMap, setCtMap] = useState({})          // { id -> raw name }
  const [ctDisplayField, setCtDisplayField] = useState({}) // { id -> displayField }
  const [selectedContentType, setSelectedContentType] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // Date range filter
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Pagination
  const [total, setTotal] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    const loadContentTypes = async () => {
      try {
        const result = await client.getContentTypes({ limit: 100, order: 'name' })
        const map = {}
        const dfMap = {}
        const types = result.items.map((ct) => {
          map[ct.sys.id] = ct.name
          dfMap[ct.sys.id] = ct.displayField
          return { id: ct.sys.id, displayName: formatContentTypeName(ct.name) }
        })
        setCtMap(map)
        setCtDisplayField(dfMap)
        setContentTypes([{ id: 'all', displayName: 'All Content Types' }, ...types])
      } catch (err) {
        console.error('Error loading content types:', err)
      }
    }
    loadContentTypes()
  }, [])

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        order: '-sys.updatedAt',
        limit: 40,
      }
      if (selectedContentType !== 'all') {
        params.content_type = selectedContentType
      }
      if (startDate) {
        params['sys.updatedAt[gte]'] = startDate + 'T00:00:00.000Z'
      }
      if (endDate) {
        params['sys.updatedAt[lte]'] = endDate + 'T23:59:59.999Z'
      }

      const result = await client.getEntries(params)
      setTotal(result.total)

      const mapped = result.items
        .filter((entry) => {
          const ctId = entry.sys.contentType.sys.id
          const df = ctDisplayField[ctId]
          const name = (df && entry.fields[df]) ? String(entry.fields[df]).trim() : getEntryName(entry.fields)
          return name !== null && name !== ''
        })
        .map((entry) => {
          const ctId = entry.sys.contentType.sys.id
          const df = ctDisplayField[ctId]
          const name = (df && entry.fields[df]) ? String(entry.fields[df]).trim() : getEntryName(entry.fields)
          return {
            id: entry.sys.id,
            name,
            contentTypeId: ctId,
            updatedAt: entry.sys.updatedAt,
            comparisonUrl: `https://app.contentful.com/spaces/${SPACE_ID}/entries/${entry.sys.id}/compare/`,
          }
        })

      setEntries(mapped)
    } catch (err) {
      console.error('Error fetching entries:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [selectedContentType, startDate, endDate, ctDisplayField])

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const params = {
        order: '-sys.updatedAt',
        limit: 40,
        skip: entries.length,
      }
      if (selectedContentType !== 'all') params.content_type = selectedContentType
      if (startDate) params['sys.updatedAt[gte]'] = startDate + 'T00:00:00.000Z'
      if (endDate) params['sys.updatedAt[lte]'] = endDate + 'T23:59:59.999Z'

      const result = await client.getEntries(params)
      setTotal(result.total)

      const mapped = result.items
        .filter((entry) => {
          const ctId = entry.sys.contentType.sys.id
          const df = ctDisplayField[ctId]
          const name = (df && entry.fields[df]) ? String(entry.fields[df]).trim() : getEntryName(entry.fields)
          return name !== null && name !== ''
        })
        .map((entry) => {
          const ctId = entry.sys.contentType.sys.id
          const df = ctDisplayField[ctId]
          const name = (df && entry.fields[df]) ? String(entry.fields[df]).trim() : getEntryName(entry.fields)
          return {
            id: entry.sys.id,
            name,
            contentTypeId: ctId,
            updatedAt: entry.sys.updatedAt,
            comparisonUrl: `https://app.contentful.com/spaces/${SPACE_ID}/entries/${entry.sys.id}/compare/`,
          }
        })

      setEntries((prev) => [...prev, ...mapped])
    } catch (err) {
      console.error('Error loading more entries:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [selectedContentType, startDate, endDate, ctDisplayField, entries.length])

  useEffect(() => {
    fetchEntries()
    const interval = setInterval(fetchEntries, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchEntries])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownOpen && !e.target.closest('.custom-select')) {
        setDropdownOpen(false)
        setSearchTerm('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const filteredContentTypes = contentTypes.filter((ct) =>
    ct.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleContentTypeSelect = (typeId) => {
    setSelectedContentType(typeId)
    setDropdownOpen(false)
    setSearchTerm('')
  }

  const clearDateFilter = () => {
    setStartDate('')
    setEndDate('')
  }

  const hasDateFilter = startDate || endDate
  const today = new Date().toISOString().split('T')[0]

  const selectedTypeName = contentTypes.find((ct) => ct.id === selectedContentType)?.displayName || 'All Content Types'

  return (
    <section className='ra-page'>
      <div className="ra-header">
        <h2 className="content_h2" style={{ margin: 0 }}>Recent Activity</h2>
        <button
          onClick={fetchEntries}
          className="ra-refresh"
          title="Refresh"
          disabled={loading}
        >
          ↻
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="ra-filters">
        {/* Content-type dropdown */}
        <div className="ra-filter-group">
          <label className="ra-filter-label">Content Type</label>
          <div className="date-input-group">
            <label className="date-sub-label">Select</label>
            <div className="custom-select">
                <div
                className="select-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                role="button"
                tabIndex={0}
                >
                <span>{selectedTypeName}</span>
                <span className="select-arrow">{dropdownOpen ? '▲' : '▼'}</span>
                </div>
                {dropdownOpen && (
                <div className="select-dropdown">
                    <input
                    type="text"
                    className="search-input"
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    />
                    <div className="options-list">
                    {filteredContentTypes.length === 0 ? (
                        <div className="no-results">No content types found</div>
                    ) : (
                        filteredContentTypes.map((ct) => (
                        <div
                            key={ct.id}
                            className={`option-item ${ct.id === selectedContentType ? 'selected' : ''}`}
                            onClick={() => handleContentTypeSelect(ct.id)}
                        >
                            {ct.displayName}
                            {ct.id === selectedContentType && (
                            <span className="checkmark">✓</span>
                            )}
                        </div>
                        ))
                    )}
                    </div>
                </div>
                )}
            </div>
          </div>
        </div>

        {/* Date range */}
        <div className="ra-filter-group">
          <label className="ra-filter-label">Date Range</label>
          <div className="date-range">
            <div className="date-input-group">
              <label className="date-sub-label">From</label>
              <input
                type="date"
                className="date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onClick={(e) => e.target.showPicker?.()}
                max={endDate ? Math.min(endDate, today) : today}
              />
            </div>
            <div className="date-input-group">
              <label className="date-sub-label">To</label>
              <input
                type="date"
                className="date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onClick={(e) => e.target.showPicker?.()}
                min={startDate || undefined}
                max={today}
              />
            </div>
            {hasDateFilter && (
              <button
                className="clear-date"
                onClick={clearDateFilter}
                title="Clear date filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Entry list ── */}
      {loading && entries.length === 0 ? (
        <div className="ra-loading">
          <Spinner size="medium" />
        </div>
      ) : error ? (
        <div className="ra-error">
          <p>Unable to load recent activity</p>
          <button onClick={fetchEntries} className="ra-retry">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="entries-list">
            {entries.length === 0 ? (
              <p className="no-entries">No recent activity</p>
            ) : (
              entries.map((entry) => (
                <a
                  title="Click to view changes in Contentful"
                  key={entry.id}
                  href={entry.comparisonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="entry-item"
                >
                  <div className="entry-content">
                    <div className="entry-type-badge">
                      {formatContentTypeName(ctMap[entry.contentTypeId] || entry.contentTypeId)}
                    </div>
                    <div className="entry-name" title={entry.name}>
                      {entry.name}
                    </div>
                    <div className="entry-time">
                      {formatRelativeTime(entry.updatedAt)}
                    </div>
                  </div>
                  <div className="entry-arrow">→</div>
                </a>
              ))
            )}
          </div>
          {entries.length < total && (
            <button
              className="load-more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : '↻ Load more'}
            </button>
          )}
        </>
      )}

      <style>{`
        .ra-page {
            flex-grow: 1;
        }
        .ra-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .ra-title {
          margin: 0;
          font-size: 22px;
          font-weight: 700;
          color: #1f2937;
        }

        .ra-refresh {
          background: none;
          border: 1px solid #d1d5db;
          font-size: 20px;
          cursor: pointer;
          color: #6b7280;
          padding: 4px 10px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .ra-refresh:hover:not(:disabled) {
          background: #f3f4f6;
          color: #1f2937;
          border-color: #9ca3af;
        }

        .ra-refresh:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ra-filters {
          display: flex;
          align-items: center;
          gap: 24px;
          flex-wrap: wrap;
          margin-bottom: 24px;
          padding: 16px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .ra-filter-group {
          flex: 1;
          min-width: 200px;
        }

        .ra-filter-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* ── Dropdown ── */
        .custom-select {
          position: relative;
        }

        .select-trigger {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          color: #1f2937;
          transition: all 0.2s;
          user-select: none;
        }

        .select-trigger:hover {
          border-color: #3b82f6;
        }

        .select-arrow {
          font-size: 10px;
          color: #6b7280;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .select-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 100;
          max-height: 300px;
          display: flex;
          flex-direction: column;
        }

        .search-input {
          padding: 10px 12px;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          font-size: 14px;
          outline: none;
          border-radius: 6px 6px 0 0;
        }

        .options-list {
          overflow-y: auto;
          max-height: 240px;
        }

        .options-list::-webkit-scrollbar { width: 6px; }
        .options-list::-webkit-scrollbar-track { background: transparent; }
        .options-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

        .option-item {
          padding: 10px 12px;
          cursor: pointer;
          font-size: 14px;
          color: #1f2937;
          transition: background 0.15s;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .option-item:hover { background: #f3f4f6; }
        .option-item.selected { background: #eff6ff; color: #3b82f6; font-weight: 500; }
        .checkmark { color: #3b82f6; font-weight: bold; }

        .no-results {
          padding: 16px 12px;
          text-align: center;
          color: #6b7280;
          font-size: 14px;
        }

        /* ── Date range ── */
        .date-range {
          display: flex;
          gap: 8px;
          align-items: flex-end;
        }

        .date-input-group {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .date-sub-label {
          font-size: 11px;
          font-weight: 500;
          color: #6b7280;
        }

        .date-input {
          padding: 10px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          color: #1f2937;
          transition: all 0.2s;
          font-family: inherit;
          background: white;
        }

        .date-input:hover { border-color: #3b82f6; }
        .date-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }

        .clear-date {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          height: 40px;
          transition: background 0.2s;
        }

        .clear-date:hover { background: #dc2626; }

        /* ── States ── */
        .ra-loading,
        .ra-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 16px;
          color: #6b7280;
        }

        .ra-error p { margin: 0 0 12px; font-size: 14px; }

        .ra-retry {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .ra-retry:hover { background: #2563eb; }

        /* ── Load more ── */
        .load-more {
          display: block;
          padding: 10px 16px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          color: #3b82f6;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 16px;
          margin: 0 auto 24px;
          text-transform: uppercase;
          font-weight: 700;
          border-color: currentColor;
        }

        .load-more:hover:not(:disabled) {
          background: #eff6ff;
          border-color: #3b82f6;
        }

        .load-more:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ── Entries ── */
        .entries-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 24px;
        }

        .entries-list::-webkit-scrollbar { width: 6px; }
        .entries-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

        .no-entries {
          text-align: center;
          color: #6b7280;
          padding: 48px 16px;
          margin: 0;
          font-size: 14px;
        }

        .entry-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          text-decoration: none;
          color: inherit;
          transition: all 0.2s;
        }

        .entry-item:hover {
          background: #f9fafb;
          border-color: #3b82f6;
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(59,130,246,.08);
        }

        .entry-content { flex: 1; min-width: 0; }

        .entry-type-badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          color: #3b82f6;
          background: #eff6ff;
          padding: 2px 8px;
          border-radius: 4px;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .entry-name {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .entry-time { font-size: 12px; color: #6b7280; }

        .entry-arrow {
          font-size: 18px;
          color: #9ca3af;
          margin-left: 12px;
          transition: transform 0.2s;
        }

        .entry-item:hover .entry-arrow {
          transform: translateX(4px);
          color: #3b82f6;
        }
      `}</style>
    </section>
  )
}

export default RecentActivityPage
